import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  AccessTime as RecentIcon,
  ChatBubbleOutline as ChatIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Chat, searchChats } from "../../services/api";
import {
  getRecentChats,
  addRecentChat,
} from "../../services/recentChatsService";

type SearchModalProps = {
  open: boolean;
  onClose: () => void;
  onSelectChat: (chatId: string) => void;
};

type ListItem = { id: string; title?: string | null; create_date: string };

export const SearchModal: React.FC<SearchModalProps> = ({
  open,
  onClose,
  onSelectChat,
}) => {
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [items, setItems] = React.useState<ListItem[]>([]);
  const [recentItems, setRecentItems] = React.useState<ListItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const formatCreateDate = (isoDate: string) => {
    const asUtc = isoDate.endsWith("Z") ? isoDate : `${isoDate}Z`;
    return new Date(asUtc).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  React.useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    setTimeout(() => inputRef.current?.focus(), 0);
    const recent = getRecentChats();
    setRecentItems(recent);
    setItems(recent);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (!trimmed) {
      setItems(recentItems);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchChats(trimmed);
        setItems(results.slice(0, 6));
        setActiveIndex(0);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, recentItems]);

  const handleSelectChat = (item: ListItem) => {
    addRecentChat({
      id: item.id,
      title: item.title ?? null,
      create_date: item.create_date,
    });
    onSelectChat(item.id);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % items.length);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
    }
    if (e.key === "Enter" && items[activeIndex]) {
      e.preventDefault();
      handleSelectChat(items[activeIndex]);
    }
    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "#1e2932",
          color: "#E6E6E6",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          width: "36rem",
        },
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Cerca tra le tue chat..."
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "rgba(230,230,230,0.8)" }} />
                </InputAdornment>
              ),
              sx: {
                px: 1,
                py: 1,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.06)",
                color: "#E6E6E6",
              },
            }}
          />
          <IconButton onClick={onClose} sx={{ color: "rgba(230,230,230,0.8)" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          maxHeight: "60vh",
          overflowY: "auto",
          minHeight: "16.4rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Typography
            variant="caption"
            sx={{ color: "rgba(230,230,230,0.65)" }}
          >
            {query.trim().length > 0 ? "Risultati chat" : "Aperte di recente"}
          </Typography>
        </Box>
        {!loading && (
          <List sx={{ pt: 0 }}>
            {items.map((chat, idx) => (
              <ListItemButton
                key={chat.id}
                selected={idx === activeIndex}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => handleSelectChat(chat)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  "&.Mui-selected": { bgcolor: "rgba(146,214,213,0.2)" },
                }}
              >
                <ListItemIcon
                  sx={{ minWidth: 34, color: "rgba(230,230,230,0.7)" }}
                >
                  {query.trim().length > 0 ? (
                    <ChatIcon fontSize="small" />
                  ) : (
                    <RecentIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={chat.title || "Chat senza titolo"}
                  secondary={formatCreateDate(chat.create_date)}
                  primaryTypographyProps={{ fontSize: 14, color: "#E6E6E6" }}
                  secondaryTypographyProps={{
                    fontSize: 12,
                    color: "rgba(230,230,230,0.65)",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {!loading && query.trim().length > 0 && items.length === 0 && (
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              flex: 1,
            }}
          >
            <Typography
              sx={{
                color: "rgba(230,230,230,0.65)",
              }}
            >
              Nessuna chat trovata.
            </Typography>
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              flex: 100,
            }}
          >
            <CircularProgress
              size={32}
              sx={{ color: "rgba(230,230,230,0.7)" }}
            />
          </Box>
        )}
        {!loading && query.trim().length === 0 && items.length === 0 && (
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              flex: 1,
            }}
          >
            <Typography sx={{ color: "rgba(230,230,230,0.65)" }}>
              Nessuna chat
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
      <Box sx={{ px: 2, py: 1.25, display: "flex", gap: 2 }}>
        <Typography variant="caption" sx={{ color: "rgba(230,230,230,0.65)" }}>
          Enter per aprire chat
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(230,230,230,0.65)" }}>
          ↑ ↓ per navigare
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(230,230,230,0.65)" }}>
          Esc per chiudere
        </Typography>
      </Box>
    </Dialog>
  );
};
